/**
 * Session-based form data storage with expiration
 * 
 * Stores form data in localStorage with a session ID and expiration time.
 * Data expires after 5 minutes of inactivity.
 */

const SESSION_STORAGE_PREFIX = 'form_session_';
const SESSION_ID_KEY = 'form_session_id';
const SESSION_EXPIRY_KEY = 'form_session_expiry';
const SESSION_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Get or create a valid session ID
 */
function isSessionValid(sessionId: string | null, expiry: string | null): boolean {
  return !!sessionId && !!expiry && Date.now() < parseInt(expiry, 10);
}

function getOrCreateSessionId(): string {
  const existingSessionId = localStorage.getItem(SESSION_ID_KEY);
  const existingExpiry = localStorage.getItem(SESSION_EXPIRY_KEY);
  
  if (isSessionValid(existingSessionId, existingExpiry)) {
    updateSessionExpiry();
    return existingSessionId!;
  }
  
  if (existingSessionId) {
    clearExpiredSession(existingSessionId);
  }
  
  const newSessionId = generateSessionId();
  localStorage.setItem(SESSION_ID_KEY, newSessionId);
  updateSessionExpiry();
  return newSessionId;
}

/**
 * Update session expiry time
 */
function updateSessionExpiry(): void {
  const expiryTime = Date.now() + SESSION_DURATION_MS;
  localStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString());
}

/**
 * Get the storage key for a form field in the current session
 */
function getSessionStorageKey(key: string, sessionId: string): string {
  return `${SESSION_STORAGE_PREFIX}${sessionId}_${key}`;
}

/**
 * Clear expired session data
 */
function clearExpiredSession(sessionId: string): void {
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(`${SESSION_STORAGE_PREFIX}${sessionId}_`)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(SESSION_EXPIRY_KEY);
}

function cleanupCurrentSession(now: number): void {
  const currentExpiry = localStorage.getItem(SESSION_EXPIRY_KEY);
  if (!currentExpiry) return;

  const expiryTime = parseInt(currentExpiry, 10);
  if (now < expiryTime) return;

  const currentSessionId = localStorage.getItem(SESSION_ID_KEY);
  if (currentSessionId) {
    clearExpiredSession(currentSessionId);
  }
}

function collectSessionIds(): Set<string> {
  const sessionIds = new Set<string>();
  const pattern = new RegExp(`${SESSION_STORAGE_PREFIX}([^_]+)_`);
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(SESSION_STORAGE_PREFIX)) continue;
    const match = key.match(pattern);
    if (match) sessionIds.add(match[1]);
  }
  return sessionIds;
}

function isSessionExpiredByTimestamp(sessionId: string, now: number): boolean {
  const parts = sessionId.split('_');
  if (parts.length < 2) return false;
  const sessionTimestamp = parseInt(parts[1], 10);
  return !isNaN(sessionTimestamp) && (now - sessionTimestamp > SESSION_DURATION_MS);
}

function collectKeysForSession(sessionId: string): string[] {
  const keys: string[] = [];
  const prefix = `${SESSION_STORAGE_PREFIX}${sessionId}_`;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  return keys;
}

/**
 * Clean up all expired sessions (called on initialization)
 */
export function cleanupExpiredSessions(): void {
  const now = Date.now();
  cleanupCurrentSession(now);

  const sessionIds = collectSessionIds();
  const keysToRemove: string[] = [];

  sessionIds.forEach(sessionId => {
    if (isSessionExpiredByTimestamp(sessionId, now)) {
      keysToRemove.push(...collectKeysForSession(sessionId));
    }
  });

  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Get a value from session storage
 */
export function getSessionFormValue(key: string): string {
  const sessionId = getOrCreateSessionId();
  const storageKey = getSessionStorageKey(key, sessionId);
  return localStorage.getItem(storageKey) ?? '';
}

/**
 * Set a value in session storage
 */
export function setSessionFormValue(key: string, value: string): void {
  const sessionId = getOrCreateSessionId();
  const storageKey = getSessionStorageKey(key, sessionId);
  localStorage.setItem(storageKey, value);
  updateSessionExpiry();
}

/**
 * Set multiple form data values in session storage
 */
export function setSessionFormData(formData: any): void {
  const sessionId = getOrCreateSessionId();
  
  Object.keys(formData).forEach((key) => {
    const value = formData[key].value ?? '';
    const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
    const storageKey = getSessionStorageKey(key, sessionId);
    localStorage.setItem(storageKey, serializedValue);
  });
  
  updateSessionExpiry();
}

/**
 * Clear the current session and all its data
 */
export function clearSessionFormData(): void {
  const sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (sessionId) {
    clearExpiredSession(sessionId);
  }
}

/**
 * Check if a field value has changed compared to session storage
 */
export function hasFieldChanged(key: string, currentValue: any): boolean {
  const storedValue = getSessionFormValue(key);
  const serializedCurrentValue = typeof currentValue === 'object' 
    ? JSON.stringify(currentValue) 
    : String(currentValue ?? '');
  return storedValue !== serializedCurrentValue;
}

/**
 * Check if any form fields have changed
 */
function resolveFormFieldValue(formData: any, key: string): any {
  const val = formData[key].value;
  return typeof val === 'object' ? val.value : val;
}

export function hasFormDataChanged(formData: any): boolean {
  return Object.keys(formData).some(key =>
    hasFieldChanged(key, resolveFormFieldValue(formData, key))
  );
}

